/**
 * Speaker profiles -- group 09 (European boutique cabinet and driver makers).
 *
 * These eight are grouped because each one differs from the others in the
 * *mechanism* it uses to control cabinet and driver behaviour rather than in
 * marketing position: a sandwich-membrane cone system with no dome tweeter, an
 * isobaric carbon monocoque, a mineral-composite standmount, a bolted aluminium
 * high-sensitivity design, a bending-wave transducer running from 180 Hz up
 * without a crossover, a coaxial ribbon, a carved solid-wood cabinet with
 * side-firing bass, and a high-sensitivity large-cone two-way. Each therefore
 * gets its own voicing, resonance set, directivity and impedance behaviour.
 *
 * Confidence is 'ESTIMATED' throughout. These manufacturers publish sensitivity,
 * impedance and dimensions inconsistently and rarely publish an alignment, so
 * where the enclosure type or bass alignment is an inference rather than a
 * published fact the provenanceNote says so explicitly. All prices are per pair
 * and approximate, since every one of these is dealer-quoted.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_09: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Zellaton',
    model: 'Reference',
    price: usd(120000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Zellaton is dealer-quoted and pricing varies widely by market.'),
    year: 2016,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 90,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [28, 23],
    hfExtension: 22000,
    crossovers: [[350, 12], [3200, 12]],
    responseTilt: [[20, 0], [60, 0.4], [150, 0.2], [400, -0.3], [1000, 0], [3000, 0.3], [7000, -0.6], [12000, -1.2], [20000, -2.0]],
    resonances: [[130, -40, 12], [420, -46, 18]],
    coverageAngleDeg: 100,
    recommendedWatts: [30, 300],
    dimensionsMm: [1280, 400, 480],
    massKg: 95,
    thdPercent: 0.18,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#d8c9a8',
    tags: ['sandwich-membrane', 'cone-tweeter', 'hand-built-drivers'],
    notes:
      'The drive units use a hard-foam core with foil skins, giving a stiffness-to-mass ratio high enough to push cone breakup above the passband, so this profile carries the lowest distortion figure and the lowest-level driver resonances in the group. The treble is produced by a small cone rather than a dome, which is why usable extension is modelled as stopping near 22 kHz and the top octave rolls away on axis instead of holding flat to 30 kHz like the ribbon entry. A sealed alignment gives a second-order low-frequency rolloff, so the response falls more slowly below f3 than the ported designs here and group delay through the bass is lower.',
    provenanceNote:
      'Sandwich-membrane driver construction, the cone treble unit and the three-way floorstanding format are from Zellaton published material. The sealed alignment is a modelling assumption -- Zellaton does not publish an enclosure alignment. Sensitivity, impedance, bass extension, crossover points, dimensions, mass, distortion and the voicing offsets are estimates, not quoted specifications. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Wilson Benesch',
    model: 'Resolution',
    price: usd(45000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; finish options move the price.'),
    year: 2019,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 7,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 6,
    minOhms: 3.4,
    bass: [30, 25],
    hfExtension: 30000,
    portTuningHz: 29,
    crossovers: [[350, 12], [5000, 12]],
    responseTilt: [[20, 0], [45, 0.6], [90, -0.4], [250, -0.6], [800, 0.2], [2000, 0.5], [4000, -0.3], [9000, 0.6], [20000, -0.4]],
    resonances: [[220, -48, 24], [600, -50, 28]],
    coverageAngleDeg: 110,
    recommendedWatts: [50, 400],
    dimensionsMm: [1180, 200, 500],
    massKg: 65,
    thdPercent: 0.25,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#3f4a56',
    tags: ['isobaric-bass', 'carbon-monocoque', 'narrow-baffle'],
    notes:
      'Bass is produced by drivers coupled in an isobaric pair, so the two motors share the same air load and their nonlinearities partly cancel; the modelled result is lower low-frequency distortion than the box volume would otherwise allow, at the cost of halved sensitivity in that band. The carbon-fibre monocoque cabinet moves panel resonances up in frequency and down in level relative to a wooden box, which is modelled as a pair of very low-level, high-Q modes rather than the low-Q lower-midrange behaviour of a thin-walled design. A narrow baffle puts the baffle step around 575 Hz, so more of the presence region depends on room boundary support than in the wide-baffle designs here.',
    provenanceNote:
      'Isobaric bass loading, carbon monocoque cabinet construction and the three-way narrow-baffle floorstanding format are from Wilson Benesch published material. The ported alignment and port tuning are modelling assumptions. Sensitivity, minimum impedance, bass extension, crossover points, dimensions, mass, distortion and the voicing offsets are estimates. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Kroma Atelier',
    model: 'Mimi',
    price: usd(15000, 'APPROXIMATE', 2021, 'Approximate US retail per pair, stands excluded.'),
    year: 2017,
    country: 'Spain',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 87,
    nominalOhms: 8,
    minOhms: 6.2,
    bass: [42, 35],
    hfExtension: 26000,
    portTuningHz: 40,
    crossovers: [[2200, 12]],
    responseTilt: [[20, 0], [80, 0.3], [200, 0.5], [700, 0], [1800, -0.9], [3500, -0.7], [7000, 0.4], [12000, 0.2], [20000, -1.0]],
    resonances: [[190, -42, 16], [520, -46, 20]],
    coverageAngleDeg: 120,
    recommendedWatts: [25, 200],
    dimensionsMm: [430, 250, 320],
    massKg: 26,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#e6e2dc',
    tags: ['mineral-composite-cabinet', 'constrained-layer-damping', 'two-way'],
    notes:
      'The cabinet is a solid mineral-composite shell with constrained-layer damping, so it controls resonance by mass and loss rather than by stiffness; the modelled modes are therefore moderate in Q and low in level, sitting between the very high-Q aluminium and carbon designs here and the deliberately lossy wooden one. Being a two-way, the crossover sits at 2.2 kHz right in the ear most sensitive region, and the authored voicing pulls that band down about one dB, which is what keeps a single crossover in the presence region from sounding forward. At 8 ohms nominal with a 6.2 ohm minimum it is the easiest load in the group and the only one a low-power valve amplifier can drive without impedance-driven response error.',
    provenanceNote:
      'Mineral-composite cabinet with constrained-layer damping and the two-way standmount format are from Kroma Atelier published material. The ported alignment, port tuning and crossover frequency are modelling assumptions. Sensitivity, impedance, bass extension, dimensions, mass, distortion and the voicing offsets are estimates. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Stenheim',
    model: 'Alumine Five',
    price: usd(60000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; finish options move the price.'),
    year: 2018,
    country: 'Switzerland',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 94,
    nominalOhms: 8,
    minOhms: 6.0,
    bass: [32, 27],
    hfExtension: 25000,
    portTuningHz: 31,
    crossovers: [[350, 12], [3000, 12]],
    responseTilt: [[20, 0], [50, 0.5], [120, 0.8], [300, -0.3], [900, 0.3], [2500, 0.6], [5000, 0.2], [10000, -0.5], [20000, -1.2]],
    resonances: [[260, -46, 26], [900, -50, 30]],
    coverageAngleDeg: 110,
    recommendedWatts: [10, 200],
    dimensionsMm: [1200, 300, 400],
    massKg: 70,
    thdPercent: 0.28,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#aab4bd',
    tags: ['aluminium-cabinet', 'high-sensitivity', 'valve-friendly'],
    notes:
      'Ninety-four dB of sensitivity into a benign 8 ohm load makes this the design in the group that turns amplifier noise into an audible variable: the residual hiss of the valve entries in the amplifier database sits roughly eight dB higher here than on the 86 dB designs. The bolted aluminium enclosure stores little energy but what it does store is high-Q, so the modelled cabinet modes are narrow and around 46 dB down rather than broad and closer to the signal. A slight lower-midrange lift near 120 Hz in the voicing is what stops a high-sensitivity three-way from sounding thin at low listening levels.',
    provenanceNote:
      'Aluminium enclosure construction, the three-way format and a sensitivity in the mid-90s dB with an 8 ohm nominal load are from Stenheim published material. The ported alignment, port tuning and crossover frequencies are modelling assumptions. Bass extension, minimum impedance, dimensions, mass, distortion and the voicing offsets are estimates. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Gobel High End',
    model: 'Epoque Aeon Fine',
    price: usd(70000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; Gobel pricing is dealer-quoted.'),
    year: 2018,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['bending-wave', 'dynamic'],
    driverCount: 5,
    wayCount: 2,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [28, 24],
    hfExtension: 30000,
    portTuningHz: 27,
    crossovers: [[180, 12]],
    responseTilt: [[20, 0], [60, 0.3], [150, -0.5], [400, 0.4], [1200, -0.3], [3000, 0.5], [8000, 0.4], [14000, -0.4], [20000, -1.0]],
    resonances: [[95, -38, 10], [300, -44, 16]],
    coverageAngleDeg: 170,
    recommendedWatts: [50, 400],
    dimensionsMm: [1370, 380, 560],
    massKg: 110,
    thdPercent: 0.3,
    harmonicShape: HARMONIC_SHAPES.planar,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#6f5b4a',
    tags: ['bending-wave', 'crossover-free-midrange', 'wide-dispersion'],
    notes:
      'A bending-wave membrane radiates from distributed modes rather than moving as a piston, so there is no single breakup peak to notch out and the distortion structure is modelled on the panel law -- low, even-order-weighted -- instead of the cone law used elsewhere in this group. One transducer covers 180 Hz to beyond 20 kHz, so there is no crossover anywhere in the band where the ear is most sensitive to phase and lobing, and the only network in the signal path is the bass handover. Radiation stays wide to far higher frequencies than a dome, which is why coverage is modelled at 170 degrees and room coupling is set high: a much larger share of what reaches the listener is reflected energy.',
    provenanceNote:
      'The bending-wave transducer, its wide radiation pattern and its use without a midrange or treble crossover are from Gobel published material. The ported alignment, port tuning and the 180 Hz bass handover are modelling assumptions. Sensitivity, impedance, bass extension, dimensions, mass, distortion and the voicing offsets are estimates. Applying the panel harmonic law to a bending-wave driver is a modelling choice. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Piega',
    model: 'Coax 711',
    price: usd(28000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; finish options move the price.'),
    year: 2018,
    country: 'Switzerland',
    speakerType: 'coaxial',
    enclosure: 'ported',
    drivers: ['ribbon', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 91,
    nominalOhms: 4,
    minOhms: 3.1,
    bass: [30, 25],
    hfExtension: 45000,
    portTuningHz: 29,
    crossovers: [[450, 12], [3500, 12]],
    responseTilt: [[20, 0], [60, 0.4], [150, -0.3], [500, -0.4], [1500, 0.2], [4000, 0.5], [8000, 0.8], [14000, 1.0], [20000, 0.6]],
    resonances: [[240, -44, 22], [700, -48, 26]],
    coverageAngleDeg: 100,
    recommendedWatts: [40, 300],
    dimensionsMm: [1200, 260, 320],
    massKg: 48,
    thdPercent: 0.22,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#c0c6cc',
    tags: ['coaxial-ribbon', 'extruded-aluminium', 'rising-top-octave'],
    notes:
      'Midrange and treble come from one coaxial ribbon assembly, so their acoustic centres coincide and there is no vertical lobing error through the upper crossover -- the reason this is tagged coaxial rather than floorstander and the reason its off-axis behaviour is modelled as smooth through 3.5 kHz. Ribbon moving mass is low enough that useful output continues past 40 kHz, and the authored voicing carries a deliberate rise of about one dB through the top two octaves, which is the characteristic this design is actually recognised by. The ribbon is driven through a matching transformer, so the load falls toward 3 ohms in the treble exactly where an amplifier has least to give.',
    provenanceNote:
      'The coaxial ribbon midrange and treble assembly, extruded aluminium cabinet and floorstanding three-way format are from Piega published material. The ported alignment, port tuning and crossover frequencies are modelling assumptions. Sensitivity, impedance minimum, bass extension, dimensions, mass, distortion and the voicing offsets are estimates. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Boenicke Audio',
    model: 'W8',
    price: usd(18000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; wood choice and option packages move the price substantially.'),
    year: 2015,
    country: 'Switzerland',
    speakerType: 'floorstander',
    enclosure: 'transmission-line',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 6,
    minOhms: 4.4,
    bass: [35, 28],
    hfExtension: 24000,
    crossovers: [{ freqHz: 3800, slopeDbOct: 6, alignment: 'first-order' }],
    responseTilt: [[20, 0], [50, -0.6], [120, 0.5], [350, 0.3], [900, -0.4], [2500, -0.6], [6000, 0.5], [12000, 0.8], [20000, -0.6]],
    resonances: [[160, -34, 8], [480, -38, 11]],
    coverageAngleDeg: 140,
    recommendedWatts: [20, 150],
    dimensionsMm: [950, 130, 190],
    massKg: 14,
    thdPercent: 0.45,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#8b6b4a',
    tags: ['solid-wood-cabinet', 'side-firing-bass', 'first-order-crossover'],
    notes:
      'A carved solid-wood cabinet is not inert and is not meant to be: the modelled resonances are around 34 dB down with low Q, an order of magnitude more energetic than the aluminium and carbon designs here but broad rather than narrow, which is a different audible mechanism rather than simply a worse one. The 130 mm baffle puts the baffle step near 885 Hz, so the lower midrange leans on boundary support, and the small radiating area means excursion-limited distortion begins rising by about 47 Hz, giving this profile the highest distortion figure in the group. A first-order crossover leaves the two drivers overlapping across more than an octave, so their vertical interference pattern is broad and shallow rather than a narrow null.',
    provenanceNote:
      'Carved solid-wood single-block cabinet, side-firing bass driver and the compact two-way floorstanding format are from Boenicke published material. The transmission-line bass loading and the first-order crossover slope are modelling assumptions rather than published specifications. Sensitivity, impedance, bass extension, dimensions, mass, distortion and the voicing offsets are estimates. Price is approximate, per pair.',
  }),

  makeSpeaker({
    mfr: 'Grandinote',
    model: 'Mach 4',
    price: usd(25000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; Grandinote pricing is dealer-quoted and varies by market.'),
    year: 2014,
    country: 'Italy',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 4,
    wayCount: 2,
    sensitivityDb: 96,
    nominalOhms: 8,
    minOhms: 6.4,
    bass: [34, 28],
    hfExtension: 22000,
    portTuningHz: 33,
    crossovers: [{ freqHz: 2600, slopeDbOct: 12, alignment: 'butterworth' }],
    responseTilt: [[20, 0], [60, 0.8], [150, 0.5], [400, -0.5], [1000, 0.4], [2500, 1.0], [5000, -0.8], [10000, 0.3], [20000, -1.6]],
    resonances: [[150, -32, 9], [430, -36, 12]],
    coverageAngleDeg: 90,
    recommendedWatts: [5, 150],
    dimensionsMm: [1150, 300, 350],
    massKg: 45,
    thdPercent: 0.4,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#9c3b3b',
    tags: ['high-sensitivity', 'large-cone-two-way', 'low-power-friendly'],
    notes:
      'The highest sensitivity in the group at 96 dB into 8 ohms, which puts amplifier noise and amplifier output impedance both squarely in play and makes this the natural partner for the low-power valve entries in the amplifier database. Large-diameter cones running up to 2.6 kHz have narrowed to roughly 90 degrees by the time the crossover arrives, so there is a directivity step at the handover that the model represents through the narrow coverage angle -- the price paid for keeping the network simple and the sensitivity high. Plywood cabinet walls are modelled as moderately lossy, sitting between the mineral-composite standmount and the solid-wood design.',
    provenanceNote:
      'High-sensitivity two-way floorstanding format with large-diameter cone drivers and a simple crossover network is from Grandinote published material; the sensitivity is recalled as being in the mid-90s dB and is not treated as exact. The ported alignment, port tuning and crossover frequency are modelling assumptions. Impedance, bass extension, dimensions, mass, distortion and the voicing offsets are estimates. Price is approximate, per pair.',
  }),
];
